import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { Canvas, useFrame } from '@react-three/fiber';
import { Points, PointMaterial } from '@react-three/drei';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import * as random from 'maath/random/dist/maath-random.esm';

const { FiArrowRight, FiPhone } = FiIcons;

function Stars(props) {
  const ref = useRef();
  const sphere = random.inSphere(new Float32Array(5000), { radius: 1.5 });
  
  useFrame((state, delta) => {
    ref.current.rotation.x -= delta / 10;
    ref.current.rotation.y -= delta / 15;
  });
  
  return (
    <group rotation={[0, 0, Math.PI / 4]}>
      <Points ref={ref} positions={sphere} stride={3} frustumCulled={false} {...props}>
        <PointMaterial
          transparent
          color="#0066ff"
          size={0.002}
          sizeAttenuation={true}
          depthWrite={false}
        />
      </Points>
    </group>
  );
}

const Hero = () => {
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* 3D Background */}
      <div className="absolute inset-0 z-0">
        <Canvas camera={{ position: [0, 0, 1] }}>
          <Stars />
        </Canvas>
      </div>
      
      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-black/80 via-black/60 to-blue-900/20 z-10" />
      
      {/* Content */}
      <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="space-y-8"
        >
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            className="inline-flex items-center space-x-2 bg-blue-600/20 border border-blue-500/30 rounded-full px-4 py-2 text-sm text-blue-300"
          >
            <span className="w-2 h-2 bg-blue-400 rounded-full animate-pulse" />
            <span>13+ Years of Excellence</span>
          </motion.div>

          {/* Main Heading */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="text-4xl md:text-6xl lg:text-7xl font-bold leading-tight"
          >
            <span className="gradient-text">Smart Electrical &</span>
            <br />
            <span className="text-white">Automation Solutions</span>
            <br />
            <span className="gradient-text">You Can Trust</span>
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto leading-relaxed"
          >
            Powering Homes, Securing Futures with cutting-edge electrical, 
            CCTV, smart home automation, and security solutions in Kashmir.
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6"
          >
            <Link to="/services" className="group flex items-center space-x-3 bg-gradient-to-r from-blue-600 to-cyan-600 px-8 py-4 rounded-full text-white font-semibold text-lg hover:from-blue-700 hover:to-cyan-700 transition-all duration-300 glow-blue-strong">
              <span>Book a Service</span>
              <SafeIcon 
                icon={FiArrowRight} 
                className="text-lg group-hover:translate-x-1 transition-transform duration-300" 
              />
            </Link>
            
            <a
              href="https://wa.me/919906669918"
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center space-x-3 border-2 border-white/20 px-8 py-4 rounded-full text-white font-semibold text-lg hover:border-blue-400 hover:bg-blue-400/10 transition-all duration-300"
            >
              <SafeIcon icon={FiPhone} className="text-lg" />
              <span>Talk on WhatsApp</span>
            </a>
          </motion.div>

          {/* Tagline */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1 }}
            className="pt-8"
          >
            <p className="text-blue-300 font-medium italic">
              "Powering Homes, Securing Futures"
            </p>
          </motion.div>
        </motion.div>
      </div>

      {/* Floating Elements */}
      <div className="absolute inset-0 z-15 pointer-events-none">
        <div className="absolute top-1/4 left-10 w-4 h-4 bg-blue-400 rounded-full animate-pulse opacity-60" />
        <div className="absolute top-1/3 right-16 w-3 h-3 bg-cyan-400 rounded-full animate-pulse opacity-40 float-animation" />
        <div className="absolute bottom-1/4 left-1/4 w-2 h-2 bg-blue-300 rounded-full animate-pulse opacity-50" />
        <div className="absolute bottom-1/3 right-1/3 w-5 h-5 bg-blue-500 rounded-full animate-pulse opacity-30 float-animation" />
      </div>
    </section>
  );
};

export default Hero;